import React, { useState, useEffect } from "react";
import { useNotification } from "../../context/NotificationContext";
import universityService from "../../services/universityService";
import campusService from "../../services/campusService";

const FacultyForm = ({ faculty, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    universityId: "",
    campusId: "",
    nameEn: "",
    nameLocal: "",
    status: true,
  });

  const [universities, setUniversities] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loadingUniversities, setLoadingUniversities] = useState(true);
  const [loadingCampuses, setLoadingCampuses] = useState(false);
  const [errors, setErrors] = useState({});

  // University search states
  const [universitySearchTerm, setUniversitySearchTerm] = useState("");
  const [filteredUniversities, setFilteredUniversities] = useState([]);
  const [showUniversityDropdown, setShowUniversityDropdown] = useState(false);
  const [universitySearchTimeout, setUniversitySearchTimeout] = useState(null);

  // Campus search states
  const [campusSearchTerm, setCampusSearchTerm] = useState("");
  const [filteredCampuses, setFilteredCampuses] = useState([]);
  const [showCampusDropdown, setShowCampusDropdown] = useState(false);
  const [campusSearchTimeout, setCampusSearchTimeout] = useState(null);

  const { showError } = useNotification();

  useEffect(() => {
    fetchUniversities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (faculty) {
      setFormData({
        universityId: faculty.universityId || "",
        campusId: faculty.campusId || "",
        nameEn: faculty.nameEn || "",
        nameLocal: faculty.nameLocal || "",
        status: faculty.status ?? true,
      });

      // Set university search term for display
      if (faculty.universityName) {
        setUniversitySearchTerm(faculty.universityName);
      }

      // Set campus search term for display
      if (faculty.campusName) {
        setCampusSearchTerm(faculty.campusName);
      }

      // Load campuses if university is selected
      if (faculty.universityId) {
        fetchCampusesByUniversity(faculty.universityId);
      }
    } else {
      setFormData({
        universityId: "",
        campusId: "",
        nameEn: "",
        nameLocal: "",
        status: true,
      });
      setUniversitySearchTerm("");
      setCampusSearchTerm("");
    }
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faculty]);

  useEffect(() => {
    if (formData.universityId) {
      fetchCampusesByUniversity(formData.universityId);
    } else {
      setCampuses([]);
      setFilteredCampuses([]);
      setFormData((prev) => ({ ...prev, campusId: "" }));
      setCampusSearchTerm("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.universityId]);

  // University search effect
  useEffect(() => {
    if (universitySearchTimeout) {
      clearTimeout(universitySearchTimeout);
    }

    const timeout = setTimeout(() => {
      filterUniversities();
    }, 300);

    setUniversitySearchTimeout(timeout);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universitySearchTerm, universities]);

  // Campus search effect
  useEffect(() => {
    if (campusSearchTimeout) {
      clearTimeout(campusSearchTimeout);
    }

    const timeout = setTimeout(() => {
      filterCampuses();
    }, 300);

    setCampusSearchTimeout(timeout);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusSearchTerm, campuses]);

  const fetchUniversities = async () => {
    try {
      setLoadingUniversities(true);
      const response = await universityService.getUniversities(0, 1000);
      if (response.success) {
        const sortedUniversities = (response.data.data || []).sort((a, b) =>
          (a.nameLocal || a.nameEn || "").localeCompare(
            b.nameLocal || b.nameEn || "",
          ),
        );
        setUniversities(sortedUniversities);
        setFilteredUniversities(sortedUniversities);
      }
    } catch (error) {
      console.error("Failed to fetch universities:", error);
      showError("Failed to load universities");
    } finally {
      setLoadingUniversities(false);
    }
  };

  const fetchCampusesByUniversity = async (universityId) => {
    try {
      setLoadingCampuses(true);
      const response = await campusService.getCampuses(
        0,
        1000,
        "",
        universityId,
      );
      if (response.success) {
        const sortedCampuses = (response.data.data || []).sort((a, b) =>
          (a.nameLocal || a.nameEn || "").localeCompare(
            b.nameLocal || b.nameEn || "",
          ),
        );
        setCampuses(sortedCampuses);
        setFilteredCampuses(sortedCampuses);
      }
    } catch (error) {
      console.error("Failed to fetch campuses:", error);
      showError("Failed to load campuses");
      setCampuses([]);
      setFilteredCampuses([]);
    } finally {
      setLoadingCampuses(false);
    }
  };

  const filterUniversities = () => {
    if (!universitySearchTerm.trim()) {
      setFilteredUniversities(universities);
      return;
    }

    const filtered = universities.filter(
      (university) =>
        (university.nameEn || "")
          .toLowerCase()
          .includes(universitySearchTerm.toLowerCase()) ||
        (university.nameLocal || "")
          .toLowerCase()
          .includes(universitySearchTerm.toLowerCase()),
    );
    setFilteredUniversities(filtered);
  };

  const filterCampuses = () => {
    if (!campusSearchTerm.trim()) {
      setFilteredCampuses(campuses);
      return;
    }

    const filtered = campuses.filter(
      (campus) =>
        (campus.nameEn || "")
          .toLowerCase()
          .includes(campusSearchTerm.toLowerCase()) ||
        (campus.nameLocal || "")
          .toLowerCase()
          .includes(campusSearchTerm.toLowerCase()),
    );
    setFilteredCampuses(filtered);
  };

  const handleUniversitySelect = (university) => {
    setFormData((prev) => ({
      ...prev,
      universityId: university.id,
      campusId: "", // Reset campus when university changes
    }));
    setUniversitySearchTerm(university.nameEn);
    setShowUniversityDropdown(false);
    setCampusSearchTerm(""); // Reset campus search

    // Clear university error if any
    if (errors.universityId) {
      setErrors((prev) => ({
        ...prev,
        universityId: "",
      }));
    }
  };

  const handleUniversitySearchChange = (e) => {
    const value = e.target.value;
    setUniversitySearchTerm(value);
    setShowUniversityDropdown(true);

    if (!value.trim()) {
      setFilteredUniversities(universities);
      setFormData((prev) => ({
        ...prev,
        universityId: "",
        campusId: "",
      }));
    }
  };

  const handleUniversitySearchFocus = () => {
    setShowUniversityDropdown(true);
  };

  const handleUniversitySearchBlur = () => {
    setTimeout(() => setShowUniversityDropdown(false), 200);
  };

  const handleCampusSelect = (campus) => {
    setFormData((prev) => ({
      ...prev,
      campusId: campus.id,
    }));
    setCampusSearchTerm(campus.nameEn);
    setShowCampusDropdown(false);

    // Clear campus error if any
    if (errors.campusId) {
      setErrors((prev) => ({
        ...prev,
        campusId: "",
      }));
    }
  };

  const handleCampusSearchChange = (e) => {
    const value = e.target.value;
    setCampusSearchTerm(value);
    setShowCampusDropdown(true);

    if (!value.trim()) {
      setFilteredCampuses(campuses);
      setFormData((prev) => ({
        ...prev,
        campusId: "",
      }));
    }
  };

  const handleCampusSearchFocus = () => {
    setShowCampusDropdown(true);
  };

  const handleCampusSearchBlur = () => {
    setTimeout(() => setShowCampusDropdown(false), 200);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.universityId) {
      newErrors.universityId = "University is required";
    }

    if (!formData.campusId) {
      newErrors.campusId = "Campus is required";
    }

    if (!formData.nameEn.trim()) {
      newErrors.nameEn = "English name is required";
    }

    if (!formData.nameLocal.trim()) {
      newErrors.nameLocal = "Local name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showError("Please fix the validation errors");
      return;
    }

    // Convert campusId to number and prepare submit data
    const submitData = {
      universityId: formData.universityId,
      campusId: formData.campusId,
      nameEn: formData.nameEn.trim(),
      nameLocal: formData.nameLocal.trim(),
      status: formData.status,
    };

    onSubmit(submitData);
  };

  const getSelectedUniversity = () => {
    return universities.find((u) => u.id === formData.universityId);
  };

  const getSelectedCampus = () => {
    return campuses.find((c) => c.id === formData.campusId);
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* University Selection */}
      <div className="mb-3">
        <label htmlFor="universityId" className="form-label fw-medium">
          University <span className="text-danger">*</span>
        </label>
        <div className="position-relative">
          <div className="input-group">
            <input
              type="text"
              name="universityNameEn"
              className={`form-control ${
                errors.universityId ? "is-invalid" : ""
              }`}
              placeholder="Search universities..."
              value={universitySearchTerm}
              onChange={handleUniversitySearchChange}
              onFocus={handleUniversitySearchFocus}
              onBlur={handleUniversitySearchBlur}
              disabled={loading || loadingUniversities}
            />
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => setShowUniversityDropdown(!showUniversityDropdown)}
              disabled={loading || loadingUniversities}
            >
              <i className="fas fa-search"></i>
            </button>
          </div>

          {/* Hidden input for form validation */}
          <input
            type="hidden"
            name="universityId"
            value={formData.universityId}
          />

          {errors.universityId && (
            <div className="invalid-feedback">{errors.universityId}</div>
          )}
          {loadingUniversities && (
            <small className="text-muted">Loading universities...</small>
          )}

          {/* Dropdown for university selection */}
          {showUniversityDropdown && filteredUniversities.length > 0 && (
            <div className="university-dropdown">
              {filteredUniversities.map((university) => (
                <button
                  key={university.id}
                  type="button"
                  className="university-dropdown-item"
                  onClick={() => handleUniversitySelect(university)}
                >
                  <div className="university-name-en">{university.nameEn}</div>
                  <div className="university-name-local">
                    {university.nameLocal}
                  </div>
                </button>
              ))}
            </div>
          )}

          {showUniversityDropdown && filteredUniversities.length === 0 && (
            <div className="university-dropdown">
              <div className="university-dropdown-item text-muted">
                No universities found
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Campus Selection */}
      <div className="mb-3">
        <label htmlFor="campusId" className="form-label fw-medium">
          Campus <span className="text-danger">*</span>
        </label>
        <div className="position-relative">
          <div className="input-group">
            <input
              type="text"
              name="campusNameEn"
              className={`form-control ${errors.campusId ? "is-invalid" : ""}`}
              placeholder="Search campuses..."
              value={campusSearchTerm}
              onChange={handleCampusSearchChange}
              onFocus={handleCampusSearchFocus}
              onBlur={handleCampusSearchBlur}
              disabled={loading || loadingCampuses || !formData.universityId}
            />
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => setShowCampusDropdown(!showCampusDropdown)}
              disabled={loading || loadingCampuses || !formData.universityId}
            >
              <i className="fas fa-search"></i>
            </button>
          </div>

          {/* Hidden input for form validation */}
          <input type="hidden" name="campusId" value={formData.campusId} />

          {errors.campusId && (
            <div className="invalid-feedback">{errors.campusId}</div>
          )}
          {loadingCampuses && (
            <small className="text-muted">Loading campuses...</small>
          )}
          {!formData.universityId && (
            <small className="text-muted">
              Please select a university first
            </small>
          )}

          {/* Dropdown for campus selection */}
          {showCampusDropdown && filteredCampuses.length > 0 && (
            <div className="university-dropdown">
              {filteredCampuses.map((campus) => (
                <button
                  key={campus.id}
                  type="button"
                  className="university-dropdown-item"
                  onClick={() => handleCampusSelect(campus)}
                >
                  <div className="university-name-en">{campus.nameEn}</div>
                  <div className="university-name-local">
                    {campus.nameLocal}
                  </div>
                </button>
              ))}
            </div>
          )}

          {showCampusDropdown && filteredCampuses.length === 0 && (
            <div className="university-dropdown">
              <div className="university-dropdown-item text-muted">
                No campuses found
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="row">
        {/* English Name */}
        <div className="col-md-6 mb-3">
          <label htmlFor="nameEn" className="form-label fw-medium">
            Faculty Name (English) <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control ${errors.nameEn ? "is-invalid" : ""}`}
            id="nameEn"
            name="nameEn"
            value={formData.nameEn}
            onChange={handleInputChange}
            placeholder="Enter faculty name in English"
            disabled={loading}
          />
          {errors.nameEn && (
            <div className="invalid-feedback">{errors.nameEn}</div>
          )}
        </div>

        {/* Local Name */}
        <div className="col-md-6 mb-3">
          <label htmlFor="nameLocal" className="form-label fw-medium">
            Faculty Name (Local) <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control ${errors.nameLocal ? "is-invalid" : ""}`}
            id="nameLocal"
            name="nameLocal"
            value={formData.nameLocal}
            onChange={handleInputChange}
            placeholder="Enter faculty name in local language"
            disabled={loading}
          />
          {errors.nameLocal && (
            <div className="invalid-feedback">{errors.nameLocal}</div>
          )}
        </div>
      </div>

      {/* Status */}
      <div className="mb-4">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="status"
            name="status"
            checked={formData.status}
            onChange={handleInputChange}
            disabled={loading}
          />
          <label className="form-check-label fw-medium" htmlFor="status">
            Faculty Status
          </label>
        </div>
        <div className="form-text">
          <i
            className={`fas ${
              formData.status
                ? "fa-check-circle text-success"
                : "fa-times-circle text-danger"
            } me-1`}
          ></i>
          Faculty is currently{" "}
          <strong>{formData.status ? "Active" : "Inactive"}</strong>
        </div>
      </div>

      {/* Preview */}
      {(formData.nameEn ||
        formData.nameLocal ||
        getSelectedUniversity() ||
        getSelectedCampus()) && (
        <div className="alert alert-light border mb-4">
          <h6 className="fw-bold mb-2">
            <i className="fas fa-eye me-2"></i>
            Preview
          </h6>
          <div className="row">
            <div className="col-md-8">
              <div className="mb-2">
                <strong>University:</strong>{" "}
                {getSelectedUniversity()?.nameEn || "Not selected"}
                {getSelectedUniversity()?.nameLocal && (
                  <span className="text-muted">
                    {" "}
                    ({getSelectedUniversity().nameLocal})
                  </span>
                )}
              </div>
              <div className="mb-2">
                <strong>Campus:</strong>{" "}
                {getSelectedCampus()?.nameEn || "Not selected"}
                {getSelectedCampus()?.nameLocal && (
                  <span className="text-muted">
                    {" "}
                    ({getSelectedCampus().nameLocal})
                  </span>
                )}
              </div>
              <div className="mb-2">
                <strong>Faculty (EN):</strong>{" "}
                {formData.nameEn || "Faculty Name (English)"}
              </div>
              <div className="mb-2">
                <strong>Faculty (Local):</strong>{" "}
                {formData.nameLocal || "Faculty Name (Local)"}
              </div>
              <div className="mb-2">
                <strong>Status:</strong>
                <span
                  className={`badge ms-2 ${
                    formData.status ? "bg-success" : "bg-danger"
                  }`}
                >
                  {formData.status ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
            <div className="col-md-4 text-center">
              <div
                className={`${
                  formData.status ? "bg-success" : "bg-secondary"
                } rounded-3 d-flex align-items-center justify-content-center`}
                style={{ width: "80px", height: "80px", margin: "0 auto" }}
              >
                <i className="fas fa-graduation-cap text-white fs-3"></i>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Actions */}
      <div className="d-flex justify-content-end gap-2">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading || loadingUniversities || loadingCampuses}
        >
          {loading ? (
            <>
              <div
                className="spinner-border spinner-border-sm me-2"
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              {faculty ? "Updating..." : "Creating..."}
            </>
          ) : (
            <>
              <i className={`fas ${faculty ? "fa-save" : "fa-plus"} me-2`}></i>
              {faculty ? "Update Faculty" : "Create Faculty"}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default FacultyForm;
